import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useContact } from '../context/ContactContext';
import './Cancellation.css';

const AIRLINES = [
  {
    name: 'United Airlines',
    logo: '✈️',
    color: '#0052a3',
    cancellationFee: 'No fee (Basic Economy: non-refundable)',
    changeFee: '$0 (Basic Economy: not allowed)',
    refundTime: '7–20 business days',
    hours24Rule: 'Yes – full refund within 24 hrs if booked 7+ days before departure',
    basicEconomy: 'Non-refundable, no changes allowed',
    standardEconomy: 'No change/cancel fees; fare difference applies',
    business: 'Fully refundable on most fares',
    firstClass: 'Fully refundable',
    notes: 'Same-day flight change available for $75. MileagePlus members may get fee waivers.',
  },
  {
    name: 'American Airlines',
    logo: '🦅',
    color: '#c8102e',
    cancellationFee: 'No fee (Basic Economy: non-refundable)',
    changeFee: '$0 (except Basic Economy)',
    refundTime: '7–10 business days',
    hours24Rule: 'Yes – full refund within 24 hrs',
    basicEconomy: 'Non-refundable, no changes',
    standardEconomy: 'No change fees; fare difference may apply',
    business: 'Fully refundable',
    firstClass: 'Fully refundable',
    notes: 'AAdvantage elite members receive additional flexibility benefits.',
  },
  {
    name: 'Delta Air Lines',
    logo: '🔺',
    color: '#003366',
    cancellationFee: 'No fee (Basic Economy: non-refundable)',
    changeFee: '$0 for most fares',
    refundTime: '7–10 business days',
    hours24Rule: 'Yes – full refund within 24 hrs',
    basicEconomy: 'Non-refundable, no changes',
    standardEconomy: 'No change/cancel fees',
    business: 'Fully refundable (Delta One)',
    firstClass: 'Fully refundable',
    notes: 'SkyMiles Medallion members get added flexibility and priority rebooking.',
  },
  {
    name: 'Southwest Airlines',
    logo: '🌞',
    color: '#304CB2',
    cancellationFee: 'No cancellation fees on any fare',
    changeFee: '$0 — no change fees ever',
    refundTime: 'Instant travel credit; cash refunds for Anytime/Business Select',
    hours24Rule: 'N/A – all tickets can be canceled anytime',
    basicEconomy: 'N/A (Wanna Get Away — travel credit)',
    standardEconomy: 'Full travel credit retained',
    business: 'Business Select — fully refundable to original payment',
    firstClass: 'N/A',
    notes: 'Southwest has the most flexible cancellation policy in the industry.',
  },
  {
    name: 'Spirit Airlines',
    logo: '💛',
    color: '#FFD700',
    cancellationFee: '$69–$119 (waived if cancelled 60+ days out)',
    changeFee: '$69 online / $99 via agent',
    refundTime: '7 business days (credit within 24 hrs)',
    hours24Rule: 'Yes – full refund within 24 hrs',
    basicEconomy: 'Non-refundable after 24 hrs',
    standardEconomy: 'Spirit Saver$ Club members get discounts on fees',
    business: 'N/A',
    firstClass: 'N/A',
    notes: 'Flight Flex add-on allows one free modification per booking.',
  },
  {
    name: 'Frontier Airlines',
    logo: '🦬',
    color: '#007A33',
    cancellationFee: '$0–$99 depending on fare & timing',
    changeFee: '$0–$99 depending on fare',
    refundTime: '7 business days for refundable; credit for others',
    hours24Rule: 'Yes – full refund within 24 hrs',
    basicEconomy: 'Economy: non-refundable',
    standardEconomy: 'Discount Den members may get waived fees',
    business: 'N/A',
    firstClass: 'N/A',
    notes: 'THE WORKS bundle includes free carry-on, seat, and free cancellation.',
  },
  {
    name: 'Alaska Airlines',
    logo: '🌊',
    color: '#00285E',
    cancellationFee: 'No fee (Saver fare: $125 cancel fee)',
    changeFee: '$0 on main cabin+; $125 on Saver',
    refundTime: '7–20 business days',
    hours24Rule: 'Yes – full refund within 24 hrs',
    basicEconomy: 'Saver fare: $125 cancellation fee',
    standardEconomy: 'No change/cancel fees',
    business: 'First Class: fully refundable',
    firstClass: 'Fully refundable',
    notes: 'Mileage Plan MVP/Gold members may have fees waived.',
  },
  {
    name: 'JetBlue Airways',
    logo: '🔵',
    color: '#0033A0',
    cancellationFee: '$0 (Blue Basic: $100 or $200 cancel fee)',
    changeFee: '$0 (Blue Basic: not allowed or $100)',
    refundTime: '7–10 business days',
    hours24Rule: 'Yes – full refund within 24 hrs',
    basicEconomy: 'Blue Basic: $100 (domestic) or $200 (international) cancel fee',
    standardEconomy: 'No change/cancel fees on Blue, Blue Extra, Mint',
    business: 'Mint: fully refundable',
    firstClass: 'N/A',
    notes: 'Mosaic members get unlimited free changes and no cancel fees.',
  },
];

const POLICIES = [
  {
    icon: '⏱️',
    title: '24-Hour Risk-Free Cancellation',
    desc: 'All U.S. airlines must allow a full refund if you cancel within 24 hours of booking, as long as your flight departs at least 7 days later. This is a DOT rule.',
    highlight: true,
  },
  {
    icon: '✈️',
    title: 'Airline-Initiated Cancellations',
    desc: 'If the airline cancels or significantly changes your flight (3+ hours for domestic, 6+ hours for international), you are entitled to a full refund to your original payment method — no voucher required.',
    highlight: true,
  },
  {
    icon: '🎫',
    title: 'Basic Economy / Saver Fares',
    desc: 'These discounted fares are typically non-refundable and non-changeable after the 24-hour window. Always review fare rules before purchasing.',
    highlight: false,
  },
  {
    icon: '💳',
    title: 'Travel Credits vs. Cash Refunds',
    desc: 'Cancellations may result in airline travel credits rather than cash refunds, depending on the fare type. Refundable tickets always return cash to the original payment method.',
    highlight: false,
  },
  {
    icon: '🛡️',
    title: 'Travel Insurance',
    desc: 'We strongly recommend purchasing travel insurance for full protection against illness, emergencies, and unforeseen cancellations that fall outside airline policy.',
    highlight: false,
  },
  {
    icon: '📞',
    title: 'Cancel Through Us',
    desc: 'Booked through MFD Holidays? Call our 24/7 support line and we\'ll handle your cancellation directly with the airline, often faster than doing it yourself.',
    highlight: true,
  },
];

const REFUND_TIMELINE = [
  { step: '1', label: 'Submit Request', desc: 'Call us or cancel online within your booking dashboard.', time: 'Day 0' },
  { step: '2', label: 'Airline Confirms', desc: 'We submit the cancellation to the airline and receive confirmation.', time: '1–2 Days' },
  { step: '3', label: 'Refund Initiated', desc: 'The airline initiates the refund to the original payment method.', time: '3–5 Days' },
  { step: '4', label: 'Credit Applied', desc: 'Your bank or card issuer applies the credit to your account.', time: '7–20 Days' },
];

const FAQS = [
  {
    q: 'Can I cancel a non-refundable ticket?',
    a: 'Most airlines allow cancellation within 24 hours of booking for a full refund. After that, non-refundable tickets typically receive a travel credit (minus fees) rather than cash. Call us at the number below for your specific situation.',
  },
  {
    q: 'What happens if the airline cancels my flight?',
    a: 'If the airline cancels your flight or makes a significant schedule change, you are entitled to a full cash refund by law (DOT regulations). You can also opt for rebooking at no charge.',
  },
  {
    q: 'How long does a refund take?',
    a: 'Credit card refunds typically take 7–20 business days after the airline processes them. Airline travel credits are usually issued within 24–72 hours.',
  },
  {
    q: 'Can I change my flight instead of cancelling?',
    a: 'Most major airlines (except Basic Economy) now allow free flight changes — you only pay the fare difference if the new flight costs more. Call us and we can find the best option for you.',
  },
  {
    q: 'I booked through MFD Holidays. How do I cancel?',
    a: 'Simply call our 24/7 support line. Our travel experts will process the cancellation with the airline on your behalf and guide you through any refund or credit options.',
  },
  {
    q: 'Does travel insurance cover cancellations?',
    a: 'Yes — travel insurance can cover cancellations due to illness, family emergencies, natural disasters, and more. Coverage varies by plan, so review your policy details carefully.',
  },
];

export default function Cancellation() {
  const { contactSettings } = useContact();
  const tfn = contactSettings?.tfn || '+1-888-859-0441';
  const tfnClean = tfn.replace(/[^0-9+]/g, '');
  const [openFaq, setOpenFaq] = useState(null);
  const [activeAirline, setActiveAirline] = useState(0);

  return (
    <div className="cp-page">
      <Helmet>
        <title>Airline Cancellation Policy & Refund Fees 2026 | MFD Holidays</title>
        <meta name="description" content="Complete guide to airline cancellation policies and fees for United Airlines, Delta, American, Southwest, Spirit, JetBlue, Alaska & Frontier. Learn about 24-hour cancellation rules, refund timelines, and how to cancel your flight." />
        <meta name="keywords" content="airline cancellation policy, flight cancellation fee, refund policy, cancel United Airlines flight, cancel American Airlines flight, cancel Delta flight, 24 hour cancellation rule, airline refund, non-refundable ticket, flight change fee" />
        <link rel="canonical" href="https://mfdholidays.ca/cancellation" />
      </Helmet>

      {/* ── Hero ── */}
      <section className="cp-hero">
        <div className="cp-hero-inner">
          <div className="cp-hero-badge">✈️ Updated for 2026</div>
          <h1>Airline Cancellation Policy<br />&amp; Refund Fees</h1>
          <p>Everything you need to know about cancelling flights, getting refunds, and avoiding unnecessary fees — for every major U.S. airline.</p>
          <div className="cp-hero-actions">
            <a href={`tel:${tfnClean}`} className="cp-cta-btn">
              📞 Cancel My Flight — Call {tfn}
            </a>
            <a href="#airlines" className="cp-outline-btn">View Airline Policies ↓</a>
          </div>
          <div className="cp-hero-stats">
            <div className="cp-stat"><span>8</span> Airlines Covered</div>
            <div className="cp-stat-sep" />
            <div className="cp-stat"><span>24/7</span> Support Available</div>
            <div className="cp-stat-sep" />
            <div className="cp-stat"><span>100%</span> DOT Compliant</div>
          </div>
        </div>
      </section>

      {/* ── Key Policies ── */}
      <section className="cp-policies">
        <div className="cp-inner">
          <h2 className="cp-section-title">Key Cancellation Rules You Must Know</h2>
          <div className="cp-policies-grid">
            {POLICIES.map((p) => (
              <div key={p.title} className={`cp-policy-card ${p.highlight ? 'highlighted' : ''}`}>
                <div className="cp-policy-icon">{p.icon}</div>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Airline Policies Table ── */}
      <section className="cp-airlines" id="airlines">
        <div className="cp-inner">
          <h2 className="cp-section-title">Cancellation & Change Fees by Airline</h2>
          <p className="cp-section-sub">Select an airline to see detailed cancellation rules, fees, and refund policies.</p>

          {/* Airline tabs */}
          <div className="cp-airline-tabs">
            {AIRLINES.map((a, i) => (
              <button
                key={a.name}
                className={`cp-airline-tab ${activeAirline === i ? 'active' : ''}`}
                style={{ '--accent': a.color }}
                onClick={() => setActiveAirline(i)}
              >
                <span className="cp-airline-tab-logo">{a.logo}</span>
                <span className="cp-airline-tab-name">{a.name.split(' ')[0]}</span>
              </button>
            ))}
          </div>

          {/* Active airline detail */}
          {(() => {
            const a = AIRLINES[activeAirline];
            return (
              <div className="cp-airline-detail" style={{ '--accent': a.color }}>
                <div className="cp-airline-detail-head">
                  <span className="cp-airline-detail-logo">{a.logo}</span>
                  <div>
                    <h3>{a.name}</h3>
                    <p>{a.notes}</p>
                  </div>
                  <a href={`tel:${tfnClean}`} className="cp-airline-call-btn">📞 Call to Cancel</a>
                </div>
                <div className="cp-airline-fee-grid">
                  <div className="cp-fee-item">
                    <span className="cp-fee-label">Cancellation Fee</span>
                    <span className="cp-fee-value">{a.cancellationFee}</span>
                  </div>
                  <div className="cp-fee-item">
                    <span className="cp-fee-label">Change Fee</span>
                    <span className="cp-fee-value">{a.changeFee}</span>
                  </div>
                  <div className="cp-fee-item">
                    <span className="cp-fee-label">Refund Timeline</span>
                    <span className="cp-fee-value">{a.refundTime}</span>
                  </div>
                  <div className="cp-fee-item">
                    <span className="cp-fee-label">24-Hour Rule</span>
                    <span className="cp-fee-value">{a.hours24Rule}</span>
                  </div>
                </div>
                <div className="cp-fare-table">
                  <h4>Cancellation by Fare Class</h4>
                  <table>
                    <thead>
                      <tr>
                        <th>Fare Class</th>
                        <th>Policy</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td>Basic Economy</td><td>{a.basicEconomy}</td></tr>
                      <tr><td>Standard Economy</td><td>{a.standardEconomy}</td></tr>
                      <tr><td>Business Class</td><td>{a.business}</td></tr>
                      <tr><td>First Class</td><td>{a.firstClass}</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </div>
      </section>

      {/* ── Full comparison table ── */}
      <section className="cp-compare">
        <div className="cp-inner">
          <h2 className="cp-section-title">Quick Comparison: All Airlines</h2>
          <div className="cp-table-wrap">
            <table className="cp-compare-table">
              <thead>
                <tr>
                  <th>Airline</th>
                  <th>Cancel Fee</th>
                  <th>Change Fee</th>
                  <th>24-Hr Rule</th>
                  <th>Refund Time</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {AIRLINES.map((a) => (
                  <tr key={a.name}>
                    <td><span className="cp-table-airline">{a.logo} {a.name}</span></td>
                    <td>{a.cancellationFee}</td>
                    <td>{a.changeFee}</td>
                    <td>{a.hours24Rule.startsWith('Yes') ? <span className="cp-yes">✔ Yes</span> : <span className="cp-na">N/A</span>}</td>
                    <td>{a.refundTime}</td>
                    <td><a href={`tel:${tfnClean}`} className="cp-table-call">📞 Call</a></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── Refund Timeline ── */}
      <section className="cp-timeline">
        <div className="cp-inner">
          <h2 className="cp-section-title">How the Refund Process Works</h2>
          <div className="cp-timeline-steps">
            {REFUND_TIMELINE.map((s, i) => (
              <div key={s.step} className="cp-timeline-step">
                <div className="cp-timeline-node">
                  <div className="cp-timeline-circle">{s.step}</div>
                  {i < REFUND_TIMELINE.length - 1 && <div className="cp-timeline-line" />}
                </div>
                <div className="cp-timeline-body">
                  <div className="cp-timeline-time">{s.time}</div>
                  <h4>{s.label}</h4>
                  <p>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="cp-faq">
        <div className="cp-inner cp-inner-narrow">
          <h2 className="cp-section-title">Frequently Asked Questions</h2>
          <div className="cp-faq-list">
            {FAQS.map((f, i) => (
              <div key={i} className={`cp-faq-item ${openFaq === i ? 'open' : ''}`}>
                <button className="cp-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span>{f.q}</span>
                  <span className="cp-faq-chevron">{openFaq === i ? '▲' : '▼'}</span>
                </button>
                {openFaq === i && <div className="cp-faq-a">{f.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="cp-final-cta">
        <div className="cp-inner">
          <div className="cp-cta-box">
            <div className="cp-cta-icon">📞</div>
            <div className="cp-cta-text">
              <h2>Need to Cancel Your Flight?</h2>
              <p>Our travel experts are available <strong>24/7</strong> to handle your cancellation, walk you through refund options, and help you rebook — fast and hassle-free.</p>
              <ul className="cp-cta-bullets">
                <li>✔ No hold times — speak to an agent instantly</li>
                <li>✔ All airlines handled — United, Delta, American, Southwest & more</li>
                <li>✔ Best refund options guaranteed</li>
                <li>✔ Free to call — no service charge</li>
              </ul>
            </div>
            <div className="cp-cta-phone">
              <a href={`tel:${tfnClean}`} className="cp-big-call-btn">
                <span className="cp-big-call-label">Call Us Now — Free</span>
                <span className="cp-big-call-num">{tfn}</span>
              </a>
              <p className="cp-cta-avail">Available 24 hours · 7 days a week</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Disclaimer ── */}
      <div className="cp-disclaimer">
        <div className="cp-inner">
          <p><strong>Disclaimer:</strong> Airline policies change frequently. The information above is for general guidance only and is current as of April 2026. Always verify cancellation terms directly with your airline or by calling our support team. MFD Holidays is an independent travel agency and is not affiliated with any airline listed.</p>
        </div>
      </div>
    </div>
  );
}
